import { useEffect, useRef, useState } from "react";
import { MagnifyingGlass, X, WarningCircle, MapPin } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { fmtDate } from "./serviceApi";

const esc = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const Hl = ({ text, tokens }) => {
  if (!text || !tokens?.length) return text || null;
  const re = new RegExp(`(${tokens.map(esc).join("|")})`, "ig");
  return text.split(re).map((p, i) => (i % 2 ? <mark key={i} className="bg-amber-200 text-slate-900 rounded px-0.5">{p}</mark> : p));
};

// Live explorer search: instant suggestions dropdown + parent filters Pane 1 / Pane 2.
export function LiveSearchBar({ value, onChange, onPick, result, loading, placeholder, hideAddress, testId = "service-search" }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrap = useRef(null);
  const items = (result?.active ? result.records : []).slice(0, 10);
  const recCount = result?.records?.length || 0;
  const propCount = result?.property_ids?.length || 0;

  useEffect(() => { setHi(0); }, [result]);
  useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", h);
    return () => document.removeEventListener("pointerdown", h);
  }, []);

  const pick = (res) => { if (!res) return; onPick(res); setOpen(false); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(items[hi] || items[0]); }
    else if (e.key === "Escape") { if (open) setOpen(false); else onChange(""); }
  };

  return (
    <div ref={wrap} className="relative flex-1 max-w-3xl" data-testid={testId}>
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder || 'Search — e.g. "635 boiler" or "10.12.26 boiler service"'}
          className="pl-9 pr-8 h-10 text-sm"
          role="combobox"
          aria-expanded={open && items.length > 0}
          aria-controls={`${testId}-suggestions`}
          data-testid={`${testId}-input`}
        />
        {value && <button type="button" onClick={() => { onChange(""); setOpen(false); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid={`${testId}-clear`}><X size={14} /></button>}
      </div>
      {open && value.trim() && result?.active && (
        <ul id={`${testId}-suggestions`} role="listbox" className="absolute z-40 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl max-h-[45vh] overflow-y-auto" data-testid={`${testId}-suggestions`}>
          {items.length === 0 && !loading && <li className="px-4 py-3 text-sm text-slate-500" data-testid={`${testId}-no-results`}>No matching service entries.</li>}
          {items.map((res, i) => (
            <li key={res.record.id} role="option" aria-selected={i === hi}>
              <button
                type="button"
                onMouseEnter={() => setHi(i)}
                onClick={() => pick(res)}
                className={`w-full text-left px-4 py-2 border-b border-slate-100 last:border-b-0 transition-colors ${i === hi ? "bg-[#eaf2ff]" : "hover:bg-slate-50"}`}
                data-testid={`${testId}-suggestion-${res.record.id}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-slate-800 truncate"><Hl text={res.record.title} tokens={result.tokens} /></span>
                  <span className="text-[10px] font-bold uppercase text-slate-400">{res.record.category_name}</span>
                  <span className="ml-auto text-[11px] text-slate-500 whitespace-nowrap">{fmtDate(res.record.service_date)}</span>
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5 truncate">
                  {!hideAddress && <><MapPin size={11} /> <Hl text={res.property?.street} tokens={result.tokens} /></>}
                  {res.record.unit && <span>{hideAddress ? "" : "· "}{res.record.unit}</span>}
                  {res.record.service_person_name && <span>· {res.record.service_person_name}</span>}
                </div>
                {res.match_context && <div className="text-[11px] text-slate-500 mt-0.5 italic truncate"><Hl text={res.match_context} tokens={result.tokens} /></div>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {value.trim() && (result?.active || result?.invalid_dates?.length > 0) && (
        <div className="mt-1 text-[11px] text-slate-500 flex items-center gap-2 flex-wrap" data-testid={`${testId}-summary`}>
          {loading ? "Searching…" : `${recCount} matching entr${recCount === 1 ? "y" : "ies"}${propCount ? ` · ${propCount} propert${propCount === 1 ? "y" : "ies"}` : ""}`}
          {result.dates?.length > 0 && <span className="px-1.5 py-[1px] rounded bg-[#eaf2ff] text-[#1f64c2] font-semibold" data-testid={`${testId}-date-chip`}>Date: {result.dates.join(", ")}</span>}
          {result.invalid_dates?.length > 0 && <span className="text-amber-600 inline-flex items-center gap-1" data-testid={`${testId}-invalid-date`}><WarningCircle size={12} /> Invalid date: {result.invalid_dates.join(", ")} (use MM.DD.YY)</span>}
        </div>
      )}
    </div>
  );
}
